// src/pages/Users.js
import { useState, useEffect, useCallback } from "react";
import Sidebar from "../components/Sidebar";
import Header  from "../components/Header";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import "../styles/users.css";
import { edtDateTime } from "../utils/edt";

const API = process.env.REACT_APP_API_URL || "https://ipanel.idigitalhub.org/api";

/* ── Permission list ─────────────────────── */
const ALL_PERMISSIONS = [
  { key:"dashboard",      label:"Dashboard",            group:"Pages" },
  { key:"revenue",        label:"Revenue",              group:"Pages" },
  { key:"offers",         label:"Offers",               group:"Pages" },
  { key:"data",           label:"Data Management",      group:"Pages" },
  { key:"esp",            label:"Email Resources",      group:"Pages" },
  { key:"domains",        label:"Domain Mgmt (view)",   group:"Pages" },
  { key:"tools",          label:"Tools",                group:"Pages" },
  { key:"users",          label:"User Management",      group:"Pages" },
  { key:"settings",       label:"Settings",             group:"Pages" },
  { key:"can_export",     label:"Export Data",          group:"Actions" },
  { key:"can_delete",     label:"Delete Records",       group:"Actions" },
  { key:"domains_manage", label:"Domain Team Manager (add/edit/delete domains)", group:"Actions" },
];

const ROLE_DEFAULTS = {
  admin:    { dashboard:true, revenue:true, offers:true, data:true, esp:true, domains:true, tools:true, users:true,  settings:true,  can_export:true,  can_delete:true,  domains_manage:true  },
  manager:  { dashboard:true, revenue:true, offers:true, data:true, esp:true, domains:true, tools:true, users:false, settings:false, can_export:true,  can_delete:false, domains_manage:false },
  employee: { dashboard:true, revenue:true, offers:true, data:false,esp:false,domains:false,tools:true, users:false, settings:false, can_export:false, can_delete:false, domains_manage:false },
};

const ROLE_COLORS = {
  admin:    { bg:"#FEF9C3", color:"#854D0E", border:"#FDE047" },
  manager:  { bg:"#DBEAFE", color:"#1E40AF", border:"#93C5FD" },
  employee: { bg:"#F0FDF4", color:"#166534", border:"#86EFAC" },
};

/* ── Role Badge ──────────────────────────── */
function RoleBadge({ role }) {
  const c = ROLE_COLORS[role] || ROLE_COLORS.employee;
  return (
    <span style={{
      background:c.bg, color:c.color,
      border:`1px solid ${c.border}`,
      borderRadius:20, padding:"3px 10px",
      fontSize:11, fontWeight:700, textTransform:"capitalize"
    }}>{role}</span>
  );
}

/* ── Status Dot ──────────────────────────── */
function StatusDot({ status }) {
  const colors = { active:"#16A34A", inactive:"#D97706", suspended:"#DC2626" };
  return (
    <span style={{ display:"flex", alignItems:"center", gap:6, fontSize:13 }}>
      <span style={{
        width:8, height:8, borderRadius:"50%",
        background: colors[status] || "#9CA3AF",
        display:"inline-block"
      }}/>
      <span style={{textTransform:"capitalize", color:"var(--text-secondary)"}}>{status}</span>
    </span>
  );
}

/* ── User Modal ──────────────────────────── */
function UserModal({ user, onClose, onSave }) {
  const isEdit = !!user?.id;

  const [form, setForm] = useState({
    name:        user?.name        || "",
    email:       user?.email       || "",
    password:    "",
    role:        user?.role        || "employee",
    status:      user?.status      || "active",
    permissions: user?.permissions || {},
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const handleRoleChange = (role) => {
    setForm(f => ({
      ...f,
      role,
      permissions: { ...ROLE_DEFAULTS[role] },
    }));
  };

  const togglePerm = (key) => {
    setForm(f => ({
      ...f,
      permissions: { ...f.permissions, [key]: !f.permissions[key] }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await onSave(form, isEdit ? user.id : null);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Error saving user.");
    } finally {
      setLoading(false);
    }
  };

  const groups = ["Pages", "Actions"];

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">{isEdit ? "Edit User" : "Add New User"}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="modal-grid">
            {/* Left column */}
            <div className="modal-col">
              <div className="field">
                <label>Full Name *</label>
                <input type="text" required value={form.name}
                  onChange={e=>setForm(f=>({...f,name:e.target.value}))}
                  placeholder="e.g. Rahul Sharma"/>
              </div>
              <div className="field">
                <label>Email Address *</label>
                <input type="email" required value={form.email}
                  onChange={e=>setForm(f=>({...f,email:e.target.value}))}
                  placeholder="rahul@idigitalhub.com"/>
              </div>
              <div className="field">
                <label>{isEdit ? "New Password (leave blank to keep)" : "Password *"}</label>
                <input type="password" value={form.password}
                  required={!isEdit}
                  onChange={e=>setForm(f=>({...f,password:e.target.value}))}
                  placeholder="Min 8 characters"/>
              </div>
              <div className="field-row">
                <div className="field">
                  <label>Role *</label>
                  <select value={form.role} onChange={e=>handleRoleChange(e.target.value)}>
                    <option value="admin">Admin</option>
                    <option value="manager">Manager</option>
                    <option value="employee">Employee</option>
                  </select>
                </div>
                <div className="field">
                  <label>Status</label>
                  <select value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Right column — permissions */}
            <div className="modal-col">
              <div className="perm-header">
                <span className="perm-title">Permissions</span>
                <span className="perm-hint">Customize access for this user</span>
              </div>

              {groups.map(group => (
                <div key={group} className="perm-group">
                  <div className="perm-group-label">{group}</div>
                  {ALL_PERMISSIONS.filter(p=>p.group===group).map(p => (
                    <label key={p.key} className="perm-row">
                      <input
                        type="checkbox"
                        checked={!!form.permissions[p.key]}
                        onChange={() => togglePerm(p.key)}
                      />
                      <span className="perm-label">{p.label}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {error && <div className="modal-error">⚠ {error}</div>}

          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-save" disabled={loading}>
              {loading ? "Saving..." : isEdit ? "Update User" : "Create User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Delete Confirm ──────────────────────── */
function DeleteModal({ user, onClose, onConfirm }) {
  const [loading, setLoading] = useState(false);
  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:400}}>
        <div className="modal-header">
          <span className="modal-title" style={{color:"#DC2626"}}>Delete User</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{padding:"20px 24px"}}>
          <p style={{color:"var(--text-primary)", marginBottom:8}}>
            Are you sure you want to delete <strong>{user.name}</strong>?
          </p>
          <p style={{color:"var(--text-secondary)", fontSize:13}}>
            This action cannot be undone. All their data will be removed.
          </p>
          <div className="modal-footer" style={{marginTop:20}}>
            <button className="btn-cancel" onClick={onClose}>Cancel</button>
            <button className="btn-delete" disabled={loading} onClick={async()=>{
              setLoading(true);
              await onConfirm(user.id);
              onClose();
            }}>
              {loading?"Deleting...":"Yes, Delete"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── MAIN PAGE ───────────────────────────── */
export default function Users() {
  const { user: me } = useAuth();
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [filter,  setFilter]  = useState("all");
  const [addModal,   setAddModal]   = useState(false);
  const [editUser,   setEditUser]   = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [toast,      setToast]      = useState("");

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data.users || []);
    } catch (err) {
      console.error("Load users error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleSave = async (form, userId) => {
    const payload = { ...form };
    if (!payload.password) delete payload.password;

    if (userId) {
      await axios.put(`${API}/users/${userId}`, payload);
      showToast("User updated successfully.");
    } else {
      await axios.post(`${API}/users`, payload);
      showToast("User created successfully.");
    }
    loadUsers();
  };

  const handleDelete = async (userId) => {
    await axios.delete(`${API}/users/${userId}`);
    showToast("User deleted.");
    loadUsers();
  };

  // Filter
  const filtered = users.filter(u => {
    const matchSearch = u.name.toLowerCase().includes(search.toLowerCase()) ||
                        u.email.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === "all" || u.role === filter;
    return matchSearch && matchFilter;
  });

  const counts = {
    all:      users.length,
    admin:    users.filter(u=>u.role==="admin").length,
    manager:  users.filter(u=>u.role==="manager").length,
    employee: users.filter(u=>u.role==="employee").length,
  };

  return (
    <div className="layout">
      <Sidebar active="users"/>
      <div className="layout-main">
        <Header title="User Management" userName={me?.name || "Admin"}/>

        <div className="content">

          {/* Stats */}
          <div className="user-stats">
            {["all","admin","manager","employee"].map(r => (
              <div key={r} className={`ustat ${filter===r?"active":""}`}
                onClick={()=>setFilter(r)}>
                <span className="ustat-num">{counts[r]}</span>
                <span className="ustat-lbl">{r==="all"?"Total Users":r.charAt(0).toUpperCase()+r.slice(1)+"s"}</span>
              </div>
            ))}
          </div>

          {/* Toolbar */}
          <div className="user-toolbar">
            <div className="search-wrap">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input
                type="text"
                placeholder="Search by name or email..."
                value={search}
                onChange={e=>setSearch(e.target.value)}
                className="search-input"
              />
            </div>
            <button className="btn-add" onClick={()=>setAddModal(true)}>
              + Add User
            </button>
          </div>

          {/* Table */}
          <div className="users-table-wrap">
            {loading ? (
              <div className="table-loading">Loading users...</div>
            ) : filtered.length === 0 ? (
              <div className="table-empty">
                <div style={{fontSize:40, marginBottom:10}}>👥</div>
                <div>No users found</div>
              </div>
            ) : (
              <table className="users-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Last Login</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u, i) => (
                    <tr key={u.id} className={u.id === me?.id ? "current-user" : ""}>
                      <td className="td-num">{i + 1}</td>
                      <td>
                        <div className="user-info">
                          <div className="user-avatar" style={{
                            background: ROLE_COLORS[u.role]?.bg || "#F3F4F6",
                            color:      ROLE_COLORS[u.role]?.color || "#374151"
                          }}>
                            {u.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="user-name">
                              {u.name}
                              {u.id === me?.id && <span className="you-tag">You</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="td-email">{u.email}</td>
                      <td><RoleBadge role={u.role}/></td>
                      <td><StatusDot status={u.status}/></td>
                      <td className="td-muted">
                        {/* Was: toLocaleDateString("en-IN", ...)  ❌ IST
                            Now: EDT date + time */}
                        {u.last_login
                          ? edtDateTime(u.last_login)
                          : "Never"}
                      </td>
                      <td className="td-muted">
                        {u.created_at
                          ? edtDateTime(u.created_at)
                          : "—"}
                      </td>
                      <td>
                        <div className="action-btns">
                          <button className="btn-edit" onClick={()=>setEditUser(u)}
                            title="Edit user">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            Edit
                          </button>
                          {u.id !== me?.id && (
                            <button className="btn-del" onClick={()=>setDeleteUser(u)}
                              title="Delete user">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

        </div>
      </div>

      {/* Modals */}
      {addModal  && <UserModal user={null}    onClose={()=>setAddModal(false)}    onSave={handleSave}/>}
      {editUser  && <UserModal user={editUser} onClose={()=>setEditUser(null)}    onSave={handleSave}/>}
      {deleteUser && <DeleteModal user={deleteUser} onClose={()=>setDeleteUser(null)} onConfirm={handleDelete}/>}

      {/* Toast */}
      {toast && (
        <div className="toast">✅ {toast}</div>
      )}
    </div>
  );
}
